/**
 * <featured-collections-tabs>
 * Tabbed featured collections. All collection panels are rendered up-front;
 * clicking a tab shows its panel, hides the others, refreshes the carousel
 * (panels start hidden so the slider can't measure widths until shown), and
 * points the "View all" link at the active collection.
 */
class FeaturedCollectionsTabs extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('.fct__tablist');
    if (!this.tablist) return;

    this.tabs = [...this.tablist.querySelectorAll('.fct__tab')];
    this.viewAll = this.querySelector('[data-fct-viewall]');
    if (this.tabs.length < 2) return;

    this.tablist.addEventListener('click', this.handleClick.bind(this));
    this.tablist.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleClick(event) {
    const tab = event.target.closest('.fct__tab');
    if (tab) this.selectTab(tab);
  }

  handleKeydown(event) {
    const current = this.tabs.indexOf(document.activeElement.closest('.fct__tab'));
    if (current === -1) return;

    let target = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = this.tabs[(current + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = this.tabs[(current - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        target = this.tabs[0];
        break;
      case 'End':
        target = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(target);
    target.focus();
  }

  selectTab(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;

    this.tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });

    if (this.viewAll && tab.dataset.collectionUrl) {
      this.viewAll.href = tab.dataset.collectionUrl;
    }

    const activePanel = document.getElementById(tab.getAttribute('aria-controls'));
    const slider = activePanel && activePanel.querySelector('carousel-slider');
    if (slider && typeof slider.refresh === 'function') {
      // Panel was display:none until now, so widths need recalculating.
      requestAnimationFrame(() => slider.refresh());
    }
  }
}

customElements.define('featured-collections-tabs', FeaturedCollectionsTabs);
